// Where the logging is decided: which sinks receive records, from which
// level, and any extra handling. Today one console sink. A backend sink, a
// ring buffer for bug reports, sampling or redaction all go here and nowhere
// else; no plugin notices.
import { configureSync, getConsoleSink, type LogLevel as LogTapeLevel, type Sink } from '@logtape/logtape';
import type { Log, LogLevel } from '@chess/api-log';
import { LogTapeLog } from './logtape-log.ts';

/** The root category every plugin's logger hangs under. */
export const ROOT = ['chess'];

export interface LoggingOptions {
  /** The lowest level that reaches the sinks. */
  lowest: LogLevel;
  /** Extra sinks beside the console, by name. */
  sinks?: Record<string, Sink>;
  /** Whether to replace the console sink rather than add to it (tests). */
  console?: boolean;
}

const toLogTape: Record<LogLevel, LogTapeLevel> = { debug: 'debug', info: 'info', warn: 'warning', error: 'error' };

/** Configures LogTape and returns the root `Log` to register as the API. */
export function configureLogging(options: LoggingOptions): Log {
  const sinks: Record<string, Sink> = { ...(options.sinks ?? {}) };
  if (options.console !== false) sinks.console = getConsoleSink();
  configureSync({
    reset: true,
    sinks,
    loggers: [
      { category: ROOT, sinks: Object.keys(sinks), lowestLevel: toLogTape[options.lowest] },
      // LogTape's own notices about misconfiguration: worth seeing, never noisy.
      { category: ['logtape', 'meta'], sinks: options.console === false ? [] : ['console'], lowestLevel: 'warning' },
    ],
  });
  return LogTapeLog.for(ROOT);
}

// esbuild substitutes process.env.NODE_ENV at build time; declared here so no Node types are needed.
declare const process: { env: { NODE_ENV?: string } };

/** Debug while developing, info in a release build. */
export function defaultLevel(): LogLevel {
  return process.env.NODE_ENV === 'production' ? 'info' : 'debug';
}
